import type { ApiToken, JobInfo, JobRun, Scope } from '@growth/client';
import { useState } from 'react';
import { PageHeader } from '../components/Layout.tsx';
import { Card, DataTable, ErrorBox, Secret } from '../components/ui.tsx';
import { fmtDateTime, fmtDuration } from '../format.ts';
import { useAction, useApi, useSession } from '../session.tsx';

function Tokens() {
  const { api } = useSession();
  const tokens = useApi<ApiToken[]>('tokens', (a) => a.listTokens());
  const action = useAction();
  const [name, setName] = useState('');
  const [scope, setScope] = useState<Scope>('read');
  const [created, setCreated] = useState<string | null>(null);
  return (
    <Card title="Tokens de API" subtitle="Para el panel, la CLI, CI y agentes de IA. «read» solo consulta; «admin» además configura. El secreto se muestra una única vez.">
      {tokens.error && <ErrorBox error={tokens.error} />}
      <DataTable
        data={{
          columns: [{ label: 'Nombre' }, { label: 'Prefijo' }, { label: 'Scopes' }, { label: 'Creado' }, { label: 'Último uso' }, { label: '' }],
          rows: (tokens.data ?? []).map((t) => [
            t.name,
            <code key="p">{t.token_prefix}…</code>,
            t.scopes.join(', '),
            fmtDateTime(t.created_at, { seconds: false }),
            fmtDateTime(t.last_used_at, { seconds: false }),
            t.revoked_at ? (
              'revocado'
            ) : (
              <button
                key="r"
                type="button"
                className="btn small danger"
                onClick={async () => window.confirm(`Revocar «${t.name}»?`) && (await action.run(() => api.revokeToken(t.id))) && tokens.reload()}
              >
                Revocar
              </button>
            ),
          ]),
        }}
      />
      <form
        className="row"
        style={{ marginTop: 12 }}
        onSubmit={async (e) => {
          e.preventDefault();
          const r = await action.run(() => api.createToken({ name, scopes: [scope] }));
          if (r) {
            setCreated(r.token);
            setName('');
            tokens.reload();
          }
        }}
      >
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="nombre (p. ej. ci-antpocalypse)" required aria-label="Nombre del token" />
        <select className="input" value={scope} onChange={(e) => setScope(e.target.value as Scope)} aria-label="Scope">
          <option value="read">read</option>
          <option value="admin">admin</option>
        </select>
        <button className="btn primary" type="submit" disabled={action.busy}>
          Crear token
        </button>
      </form>
      {created && (
        <div className="notice stack" style={{ marginTop: 10 }}>
          <span>Cópialo ahora: no se volverá a mostrar.</span>
          <Secret value={created} />
        </div>
      )}
      {action.error && <ErrorBox error={action.error} />}
    </Card>
  );
}

function Jobs() {
  const { api } = useSession();
  const jobs = useApi<JobInfo[]>('jobs', (a) => a.listJobs());
  const runs = useApi<JobRun[]>('jobruns', (a) => a.jobRuns(30), { refreshMs: 10_000 });
  const action = useAction();
  return (
    <Card title="Jobs" subtitle="Idempotentes y con bloqueo: ejecutarlos dos veces deja el mismo estado. El worker los lanza solo (derive cada 15 s, purge a diario).">
      <DataTable
        data={{
          columns: [{ label: 'Job' }, { label: 'Descripción' }, { label: '' }],
          rows: (jobs.data ?? []).map((j) => [
            <code key="n">{j.name}</code>,
            j.description,
            j.name === 'rebuild' ? (
              <span key="r" className="muted small">
                CLI: growth jobs run rebuild --game …
              </span>
            ) : (
              <button key="r" type="button" className="btn small" disabled={action.busy} onClick={async () => (await action.run(() => api.runJob(j.name))) && runs.reload()}>
                Ejecutar
              </button>
            ),
          ]),
        }}
      />
      {action.error && <ErrorBox error={action.error} />}
      <h3 style={{ margin: '16px 0 6px' }}>Ejecuciones recientes</h3>
      <DataTable
        data={{
          columns: [{ label: 'Job' }, { label: 'Estado' }, { label: 'Inicio' }, { label: 'Duración', numeric: true }, { label: 'Resultado' }],
          rows: (runs.data ?? []).map((r) => [
            r.job_name,
            r.status,
            fmtDateTime(r.started_at),
            r.finished_at ? fmtDuration((Date.parse(r.finished_at) - Date.parse(r.started_at)) / 1000) : '—',
            <span key="s" className="muted small mono">
              {r.error ?? (r.stats ? JSON.stringify(r.stats).slice(0, 120) : '')}
            </span>,
          ]),
        }}
      />
    </Card>
  );
}

export function AdminPage() {
  const { isAdmin } = useSession();
  return (
    <>
      <PageHeader title="Tokens y jobs" sub="Administración de la plataforma (común a todos los juegos)." />
      {!isAdmin ? (
        <div className="notice">Tu token es de solo lectura.</div>
      ) : (
        <div className="stack">
          <Tokens />
          <Jobs />
        </div>
      )}
    </>
  );
}
